import { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonBackButton,
  IonButtons,
  IonList,
  IonItem,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonBadge,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";

import TeamsApi from "../../../api/teams";
import { User, Settings } from "../../../interfaces";
import styles from "./styles.team.module.css";

interface TeamsForm {
  email: string;
  role: string;
}

interface TeamMember {
  id: string;
  email: string;
  role: string;
  accepted: boolean;
  createdAt: string;
}

const Team: React.FC<ILayoutProps> = ({ settings, onRequestSettings }) => {
  const intl = useIntl();
  const [present] = useIonToast();
  const [formData, setFormData] = useState<TeamsForm>({
    email: "",
    role: "user",
  });
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [savingInvite, setSavingInvite] = useState(false);
  const [workingMemberId, setWorkingMemberId] = useState<string | null>(null);

  const showToast = (message: string, color: "success" | "danger") => {
    present({
      message,
      duration: 4000,
      color,
      position: "top",
    });
  };

  const getErrorMessage = (err: any) => {
    if (!err) {
      return intl.formatMessage({ id: "common.tryItLater" });
    }
    if (err.error && err.error[intl.locale]) {
      return err.error[intl.locale];
    }
    if (typeof err.message === "string" && err.message.trim()) {
      return err.message;
    }
    return intl.formatMessage({ id: "common.tryItLater" });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchMembers = async () => {
    setLoadingList(true);
    const { data, err } = await TeamsApi.getInvite();
    if (err) {
      showToast(getErrorMessage(err), "danger");
      setLoadingList(false);
      return;
    }
    setMembers(data?.list || []);
    setLoadingList(false);
  };

  useEffect(() => {
    void fetchMembers();
    onRequestSettings();
    // eslint-disable-next-line
  }, []);

  const handleInvite = async () => {
    if (!formData.email.trim()) return;
    setSavingInvite(true);
    const { data, err } = await TeamsApi.createInvite({
      email: formData.email.trim(),
      role: formData.role,
    });
    setSavingInvite(false);
    if (err || !data) {
      showToast(getErrorMessage(err), "danger");
      return;
    }

    setFormData((prev) => ({ ...prev, email: "" }));
    showToast(intl.formatMessage({ id: "teams.message.success" }), "success");
    await fetchMembers();
    onRequestSettings();
  };

  const handleRoleChange = async (memberId: string, role: string) => {
    setWorkingMemberId(memberId);
    const { err } = await TeamsApi.updateUserRole({ id: memberId, role });
    setWorkingMemberId(null);
    if (err) {
      showToast(getErrorMessage(err), "danger");
      return;
    }

    await fetchMembers();
  };

  const handleRemove = async (member: TeamMember) => {
    setWorkingMemberId(member.id);
    const { err } = await TeamsApi.removeInvite({ id: member.id });
    setWorkingMemberId(null);
    if (err) {
      showToast(
        intl.formatMessage({ id: "invitedList.message.deleteError" }),
        "danger",
      );
      return;
    }

    showToast(
      intl.formatMessage({ id: "invitedList.message.deleteSuccess" }),
      "success",
    );
    await fetchMembers();
    onRequestSettings();
  };

  return (
    <IonPage id="main-content">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref={`/settings`}
              text={intl.formatMessage({ id: "common.back" })}
            />
          </IonButtons>
          <IonTitle>
            <FormattedMessage id="settings.teams" />
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen color="light">
        <div className="view-wrapper">
          <div className={styles.sectionTitle}>
            <FormattedMessage id="teams.inviteMember" />
          </div>
          <div className={styles.sectionHint}>
            <FormattedMessage id="teams.subTitle" />
          </div>

          <IonList inset={true}>
            <IonItem>
              <IonInput
                type="email"
                value={formData.email}
                onIonInput={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    email: e.detail.value || "",
                  }))
                }
                label={intl.formatMessage({ id: "teams.emailLabel" })}
                placeholder="example@visito.com"
                autocomplete="email"
                clearInput
              />
            </IonItem>
            <IonItem>
              <IonSelect
                value={formData.role}
                label={intl.formatMessage({ id: "teams.selectorLabel" })}
                onIonChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    role: e.detail.value || "user",
                  }))
                }
              >
                <IonSelectOption value="user">
                  <FormattedMessage id="teams.rol.user" />
                </IonSelectOption>
                <IonSelectOption value="admin">
                  <FormattedMessage id="teams.rol.admin" />
                </IonSelectOption>
              </IonSelect>
            </IonItem>
          </IonList>
          <div className={styles.ctaWrap}>
            <IonButton
              expand="block"
              size="small"
              className={styles.inviteButton}
              disabled={savingInvite || !formData.email.trim()}
              onClick={handleInvite}
            >
              {savingInvite ? (
                <>
                  <FormattedMessage id="common.loading" />...
                </>
              ) : (
                <FormattedMessage id="teams.button" />
              )}
            </IonButton>
          </div>

          <div className={styles.sectionTitle}>
            <FormattedMessage id="invitedList.Members" />
          </div>
          <div className={styles.sectionHint}>
            <FormattedMessage
              id="invitedList.Invites"
              values={{ count: settings.status?.plan?.userLeft ?? 0 }}
            />
          </div>

          <IonList inset={true}>
            {loadingList ? (
              <IonItem>
                <IonSpinner name="dots" />
              </IonItem>
            ) : members.length === 0 ? (
              <IonItem>
                <IonLabel color="medium">
                  <FormattedMessage id="teams.subTitle" />
                </IonLabel>
              </IonItem>
            ) : (
              members.map((member) => (
                <IonItemSliding key={member.id}>
                  <IonItem>
                    <IonLabel className={styles.memberLabel}>
                      <div className={styles.memberEmail}>{member.email}</div>
                    <div className={styles.memberMeta}>
                      {!member.accepted ? (
                        <IonBadge color="warning">
                          <FormattedMessage id="leads.payments.pending" />
                        </IonBadge>
                      ) : null}
                      <span>{formatDate(member.createdAt)}</span>
                    </div>
                  </IonLabel>
                    <div className={styles.memberControls} slot="end">
                      {member.accepted ? (
                        <IonSelect
                          interface="popover"
                          value={member.role}
                          className={styles.roleSelect}
                          disabled={workingMemberId === member.id}
                          onIonChange={(e) =>
                            handleRoleChange(member.id, e.detail.value)
                          }
                        >
                          <IonSelectOption value="user">
                            <FormattedMessage id="teams.rol.user" />
                          </IonSelectOption>
                          <IonSelectOption value="admin">
                            <FormattedMessage id="teams.rol.admin" />
                          </IonSelectOption>
                        </IonSelect>
                      ) : null}
                    </div>
                  </IonItem>
                  <IonItemOptions side="end">
                    <IonItemOption
                      color="medium"
                      disabled={workingMemberId === member.id}
                      onClick={() => handleRemove(member)}
                    >
                      <FormattedMessage
                        id={member.accepted ? "common.remove" : "common.cancel"}
                      />
                    </IonItemOption>
                  </IonItemOptions>
                </IonItemSliding>
              ))
            )}
          </IonList>
        </div>
      </IonContent>
    </IonPage>
  );
};

interface ILayoutProps {
  onRequestSettings: Function;
  user: User;
  settings: Settings;
}

export default Team;
